import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import previuLogo from '../assets/previu-logo.jpeg';

import Icon from '../components/Icon';
const TABS = [
  { id: 'email',    label: 'Email & Password' },
  { id: 'otp',      label: 'WhatsApp' },
];

export default function LoginPage() {
  const { login, googleLogin, requestOTP, verifyOTP, loading, error, setError } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('email');

  // Email/password state
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');

  // WhatsApp OTP state
  const [phone, setPhone]     = useState('');
  const [otp, setOtp]         = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  function startResendTimer() {
    setResendTimer(30);
    const id = setInterval(() => setResendTimer(t => { if (t <= 1) { clearInterval(id); return 0; } return t - 1; }), 1000);
  }

  // Sign in with Google, which hands back a signed ID token (`credential`)
  // rather than the opaque access token the old implicit flow gave us.
  //
  // That swap is the point. An access token says nothing about which
  // application asked for it, so the backend could not tell a token minted for
  // Previu from one minted for any other site a staff member had signed into —
  // and it accepted both. An ID token carries an audience claim and a
  // signature, so the server verifies both before trusting a word of it.
  //
  // hosted_domain only *hints* to Google which account to offer. It is a
  // convenience, never the check: the @previu.health rule is enforced
  // server-side, where a caller cannot edit it out.
  const handleGoogleSuccess = async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      setError('Google sign-in failed. Please try again.');
      return;
    }
    const ok = await googleLogin(idToken);
    if (ok) navigate('/dashboard');
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    const ok = await login(email, password);
    if (ok) navigate('/dashboard');
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    const ok = await requestOTP(phone);
    if (ok) { setOtpSent(true); startResendTimer(); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const ok = await verifyOTP(phone, otp);
    if (ok) navigate('/dashboard');
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    const ok = await requestOTP(phone);
    if (ok) startResendTimer();
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setError('');
    setOtpSent(false);
    setOtp('');
  };

  return (
    <div className="login-page">
      <div className="login-card fade-in-up">

        {/* ── Branding ─────────────────────────────────────── */}
        <div className="login-logo-area">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: -6 }}>
            <img src={previuLogo} alt="Previu" style={{ height: 140, width: 'auto' }} />
          </div>
          <div className="login-tagline">Patient Management Platform</div>
        </div>

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'var(--amber-bg)', borderRadius: 99,
          padding: '5px 14px', fontSize: 12, color: 'var(--brand-accent)',
          fontWeight: 700, marginBottom: 20,
        }}>
          Navigator Portal
        </div> 

        <div className="login-title">Welcome back</div>
        <div className="login-sub">Sign in to access the platform</div>

        {error && (
          <div className="alert alert-error">
            <Icon name="alert" size={14} /> {error}
          </div>
        )}

        {/* ── Tab switcher ─────────────────────────────────── */}
        <div style={{
          display: 'flex', borderRadius: 10, overflow: 'hidden',
          border: '1px solid var(--border)', marginBottom: 20,
        }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => switchTab(tab.id)}
              style={{
                flex: 1, padding: '9px 4px', fontSize: 12, fontWeight: 600,
                border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                background: activeTab === tab.id ? 'var(--brand-dark)' : 'var(--bg-canvas)',
                color: activeTab === tab.id ? 'var(--text-on-brand-dark)' : 'var(--text-secondary)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── WhatsApp OTP tab ─────────────────────────────── */}
        {activeTab === 'otp' && (
          !otpSent ? (
            <form onSubmit={handleRequestOtp}>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label" htmlFor="nav-whatsapp">WhatsApp Number</label>
                <input
                  id="nav-whatsapp"
                  className="login-input"
                  type="tel"
                  placeholder="98765 43210"
                  value={phone}
                  onChange={e => { setPhone(e.target.value); setError(''); }}
                  required
                  autoComplete="tel"
                />
              </div>
              <button
                className="login-btn"
                type="submit"
                disabled={loading || !phone.trim()}
                style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
              >
                {loading ? 'Sending…' : 'Send OTP on WhatsApp'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <div style={{
                background: 'var(--bg-canvas)', borderRadius: 8, padding: '10px 14px',
                marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span>OTP sent to <strong>{phone}</strong></span>
                <button
                  type="button"
                  onClick={() => { setOtpSent(false); setOtp(''); setError(''); }}
                  style={{ fontSize: 12, color: 'var(--brand-blue)', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Change
                </button>
              </div>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label" htmlFor="nav-otp-code">Enter 6-digit OTP</label>
                <input
                  id="nav-otp-code"
                  className="login-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }}
                  required
                  autoComplete="one-time-code"
                  style={{ letterSpacing: 6, fontSize: 22, textAlign: 'center' }}
                />
              </div>
              <button className="login-btn" type="submit" disabled={loading || otp.length !== 6}>
                {loading ? 'Verifying…' : 'Verify OTP →'}
              </button>
              <div style={{ marginTop: 14, textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>
                Didn't receive it?{' '}
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0}
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 600,
                    color: resendTimer > 0 ? 'var(--text-secondary)' : '#25D366',
                    cursor: resendTimer > 0 ? 'default' : 'pointer' }}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )
        )}

        {/* ── Email & Password tab ─────────────────────────── */}
        {activeTab === 'email' && (
          <form onSubmit={handleEmailLogin} noValidate>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label" htmlFor="nav-email">Email Address</label>
              <input
                id="nav-email"
                className="login-input"
                type="email"
                placeholder="navigator@previu.health"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                required
                autoComplete="email"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label" htmlFor="nav-password" style={{ display: 'flex', justifyContent: 'space-between' }}>
                Password
                <span style={{ color: 'var(--brand-blue)', cursor: 'pointer', textTransform: 'none', fontSize: 12 }}>
                  Forgot password?
                </span>
              </label>
              <input
                id="nav-password"
                className="login-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                required
                autoComplete="current-password"
              />
            </div>
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In →'}
            </button>
          </form>
        )}

        {/* ── Divider ──────────────────────────────────────── */}
        <div className="login-divider"><span>or</span></div>

        {/* ── Previu SSO ────────────────────────────────────── */}
        {/*
          Google's own button, not our .login-sso-btn. The ID token flow renders
          through Google Identity Services, which draws the button inside an
          iframe we cannot style — and Google's branding terms require their
          button here anyway. The wrapper centres it so it sits in the same
          place the old full-width button did.
        */}
        <div className="login-sso-google" style={{ display: 'flex', justifyContent: 'center' }}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google sign-in failed. Please try again.')}
            hosted_domain="previu.health"
            text="signin_with"
            shape="rectangular"
            width="320"
          />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 5 }}>
          For @previu.health Google accounts only
        </div>

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
          Are you a patient?{' '}
          <a href="/login" style={{ color: 'var(--brand-blue)', fontWeight: 600, textDecoration: 'none' }}>
            Patient login →
          </a>
        </div>

        <div style={{ marginTop: 8, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
          Are you a subscriber?{' '}
          <a href="/subscriber-login" style={{ color: 'var(--brand-blue)', fontWeight: 600, textDecoration: 'none' }}>
            Subscriber login →
          </a>
        </div>

        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
          Secured by Previu Health · v2.0
        </div>
      </div>
    </div>
  );
}
